import {
  ButtonType,
  Selectors,
  useWidgetState,
} from '@eCommerce/frontend-legacy-shared-library';
import {StatusType} from 'models/StatusTypes';
import {supportPhoneNumber} from 'models/supportPhoneNumber';
import {Link, Text, View} from 'native-base';
import {useState} from 'react';
import {ROUTES} from 'routes';

import PanelContainer from 'components/PanelContainer';
import StatusDisplay from 'components/StatusDisplay';
import {useDismissBehavior} from 'hooks/useDismissBehavior';
import {useIsOpenWebView} from 'hooks/useIsOpenWebView';
import {useNavigateWithParams} from 'hooks/useNavigateWithParams';

export const GENERIC_ERROR_TITLE = 'SYSTEM ERROR';

export interface ErrorPanelProps {
  title?: string;
  onRetryPress: any;
  onCancelPress: any;
  retryInProgress?: boolean;
}

const ErrorPanel: React.FunctionComponent<ErrorPanelProps> = (props) => {
  const {
    title = GENERIC_ERROR_TITLE,
    onCancelPress,
    onRetryPress,
    retryInProgress,
  } = props;

  const errorCode = useWidgetState(Selectors.mfaErrorCodeSelector) as string;
  const errorMessage = useWidgetState(
    Selectors.mfaErrorMessageSelector,
  ) as string;

  const hasUserFriendlyError = !!errorCode?.includes('WCMF10');
  const userFriendlyCode = hasUserFriendlyError && errorCode?.slice(-2);
  const hasUserFriendlyErrorMessage = !!errorMessage;
  const userFriendlyErrorMessage =
    hasUserFriendlyErrorMessage &&
    errorMessage?.replace('{support number}.', '');
  const showUserFriendlyError =
    !!userFriendlyCode && !!userFriendlyErrorMessage;

  const navigate = useNavigateWithParams();
  const standardDismissBehavior = useDismissBehavior('mfa close');
  const isOpenWebView = useIsOpenWebView();

  const exitFlow = () => {
    if (isOpenWebView) {
      window.NativeAppWebView.closeWebView();
    } else {
      standardDismissBehavior();
    }
  };

  const upperDescriptionText = (
    <View>
      <Text>A system error occurred, but we’re working on it.</Text>
      <View flexDir="row" justifyContent="center">
        <Text>Please call </Text>
        <Link
          _hover={{
            textDecoration: 'underline',
          }}
          href={`tel:${supportPhoneNumber}`}
        >
          <Text color="tuna" fontFamily="body" bold>
            {supportPhoneNumber}
          </Text>
        </Link>
      </View>
    </View>
  );
  const [retriesLeft, setRetriesLeft] = useState(2);

  const handleRetryPress = () => {
    setRetriesLeft(retriesLeft - 1);
    onRetryPress();
  };

  const errorDescriptionText = (
    <View>
      <Text>{userFriendlyErrorMessage}</Text>
      <View flexDir="row" justifyContent="center">
        <Link
          _hover={{
            textDecoration: 'underline',
          }}
          href={`tel:${supportPhoneNumber}`}
        >
          <Text color="tuna" fontFamily="body" bold>
            {supportPhoneNumber}
          </Text>
        </Link>
      </View>
      <View flexDir="row" justifyContent="center">
        <Text>Error Code {userFriendlyCode}</Text>
      </View>
    </View>
  );

  const message = showUserFriendlyError
    ? errorDescriptionText
    : retriesLeft > 0
    ? 'A system error occurred, but we’re working on it. Please try again.'
    : upperDescriptionText;

  return (
    <PanelContainer
      isLoading={retryInProgress}
      title={title}
      primaryTitle={retriesLeft > 0 ? 'Try Again' : undefined}
      secondaryTitle={'Cancel'}
      onSecondaryPress={exitFlow}
      textLink={onCancelPress !== undefined ? 'Cancel' : undefined}
      onPrimaryPress={retriesLeft > 0 ? handleRetryPress : undefined}
      onTextLinkPress={onCancelPress}
      isDialogBox={false}
      secondaryType={ButtonType.SECONDARY}
      justifySecondaryForMD={true}
    >
      <StatusDisplay
        minHeight="248px"
        titleText="WELL, THIS IS EMBARRASSING."
        upperDescriptionText={message}
        statusType={StatusType.ERROR}
      />
    </PanelContainer>
  );
};

export default ErrorPanel;
